import type { Dispatch } from "react";
import { useMemo, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AxiosError } from "axios";
import axios from "axios";
import {
  PrimaryButtonMediumV2,
  SecondaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { FilePickerUncontrolled } from "../../../../../../components/FilePicker/FilePickerUncontrolled";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import {
  TextMdBold,
  TextMdRegular,
  TextXlBold,
} from "../../../../../../components/Typography/TypographyV2";
import { Form } from "../../../../../../layout/FormLayout";
import { endpoints } from "../../../../../../endpoints";
import { useStoreState } from "../../../../../../util/util";
import type {
  OptionType,
  ProductUploadSchema,
} from "../../../../../../types/types";
import type { UploadNotification } from "../../../../../../types/types.PIM";
import { UploadPopups } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/UploadPopups";
import type {
  EntityUploadAction,
  EntityUploadState,
} from "./createEntityFromUploads.util";
import {
  DownloadTemplateContainer,
  RegularTextBlock,
  TemplateButtonContainer,
  replaceDotsWithSpecialCharacter,
  revertSpecialCharacterReplace,
  getFileDateString,
} from "./createEntityFromUploads.util";
import { useTheme } from "styled-components";

const getUploadNotificationMessage = (
  t: TFunction,
  status: UploadNotification["status"],
  uploadId: string
): string => {
  switch (status) {
    case "successful":
      return t(
        "Your files have been processed successfully, and your entities have been created/updated. You will receive an email confirmation shortly."
      );
    case "failed":
      return t(
        "There was an error processing your files and your entity data could not be uploaded. Download the file corresponding to Upload ID {{uploadId}} on the next page.",
        { uploadId }
      );
    case "in_progress":
      return t(
        "Your files are currently being uploaded and you will receive an email notification once your data has been processed. You may exit the page and return once your files have been processed."
      );
    default:
      return "";
  }
};

export const UploadEntityTemplateFiles = ({
  template,
  dispatch,
  collectionFileNames,
}: {
  template: OptionType<string>;
  dispatch: Dispatch<EntityUploadAction>;
  collectionFileNames: Record<string, string>;
}) => {
  const { t } = useTranslation();

  return (
    <DownloadTemplateContainer>
      <TextXlBold as="h3" style={{ margin: "0 0 4px" }}>
        {t("Upload Entity Data")}
      </TextXlBold>
      <TextMdRegular as="p" color="secondary" style={{ margin: "0 0 32px" }}>
        {t(
          "Once you have updated your entity data, upload the CSV files for Groups & Collections to create & update your entities."
        )}
      </TextMdRegular>
      <UploadEntityCSVFiles
        template={template}
        dispatch={dispatch}
        collectionFileNames={collectionFileNames}
      />
    </DownloadTemplateContainer>
  );
};

const UploadEntityCSVFiles = ({
  template,
  dispatch,
  collectionFileNames,
}: {
  template: OptionType<string>;
  dispatch: Dispatch<EntityUploadAction>;
  collectionFileNames: Record<string, string>;
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [uploadId, setUploadId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] =
    useState<UploadNotification["status"]>("in_progress");

  const { t } = useTranslation();
  const { notifyError, pollNotification } = useNotifications();
  const { tenant_id } = useStoreState();
  const theme = useTheme();

  const groupFileName = useMemo(
    () =>
      replaceDotsWithSpecialCharacter(
        `${template.label.toLowerCase()}_groups_${getFileDateString()}`
      ),
    [template.label]
  );

  const UploadSchema = useMemo(() => {
    const fieldSchemas = Object.keys(collectionFileNames).reduce<
      Record<string, z.ZodTypeAny>
    >(
      (acc, collectionName) => {
        acc[replaceDotsWithSpecialCharacter(collectionName)] = z
          .instanceof(File)
          .nullable()
          .optional();
        return acc;
      },
      { [groupFileName]: z.instanceof(File).nullable().optional() }
    );

    return z.object(fieldSchemas).superRefine((formValue, ctx) => {
      Object.entries(formValue).forEach(([expectedFieldName, file]) => {
        const expectedFileName = `${revertSpecialCharacterReplace(
          expectedFieldName
        )}.csv`;
        if (file && expectedFileName !== file.name) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              "Uploaded file name is different from expected file name, {{expectedFileName}}",
              { expectedFileName }
            ),
            path: [expectedFieldName],
          });
        }
      });
    });
  }, [collectionFileNames, groupFileName, t]);

  const methodsOfUseForm = useForm({ resolver: zodResolver(UploadSchema) });
  const { handleSubmit, watch } = methodsOfUseForm;

  // watch() subscribes to form changes so the disabled state stays in sync
  const formValues = watch();
  const formHasSelectedFiles = Object.values(formValues).some(Boolean);

  const getPollFn = (uploadId: string) => async () => {
    const {
      data: { status, upload_number },
    } = await axios.get<UploadNotification>(
      endpoints.v2_tenants_tenant_id_product_uploads_upload_id_notifications(
        tenant_id,
        uploadId
      )
    );
    const message = getUploadNotificationMessage(t, status, upload_number);
    return {
      shouldPoll: status === "in_progress",
      status,
      message,
      uploadId: upload_number,
    };
  };

  const onCompletePolling = ({
    status,
    uploadId,
    message,
  }: {
    status: UploadNotification["status"];
    message: string;
    uploadId: string;
    [key: string]: string | number | boolean | object;
  }) => {
    setStatus(status);
    setDialogMessage(message);
    setUploadId(uploadId);
    setShowDialog(true);
  };

  const onSubmit = async (value: Record<string, File | null | undefined>) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(value).forEach(([fieldName, file]) => {
        if (file?.name) {
          formData.append(revertSpecialCharacterReplace(fieldName), file);
        }
      });

      const { data: uploads } = await axios.post<ProductUploadSchema[]>(
        endpoints.v2_tenants_tenant_id_pim_products_upload(tenant_id),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (uploads.length > 0) {
        const newUploadId = uploads[0].upload_number;
        setStatus("in_progress");
        setUploadId(newUploadId);
        setDialogMessage(
          getUploadNotificationMessage(t, "in_progress", newUploadId)
        );
        setShowDialog(true);
        pollNotification({
          pollFn: getPollFn(newUploadId),
          onComplete: onCompletePolling,
        });
      }
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ??
          t(
            "There was an error submitting your uploads. Please try again later."
          ),
        { error }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onComplete = () => {
    dispatch({ type: "complete", payload: {} as EntityUploadState });
  };

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <TextMdBold style={{ margin: 0 }}>{t("Groups")}</TextMdBold>
        <RegularTextBlock style={{ marginBottom: theme.resolve("spacing_xl") }}>
          {t("This file is required to create entity profiles")}
        </RegularTextBlock>
        <FilePickerUncontrolled
          methodsOfUseForm={methodsOfUseForm}
          placeHolderText={t("Drag and drop {{csv_file}}, OR", {
            csv_file: `${groupFileName}.csv`,
          })}
          name={groupFileName}
          accept=".csv"
          required={false}
        />

        {Object.keys(collectionFileNames).length > 0 && (
          <>
            <TextMdBold style={{ margin: 0 }}>{t("Collections")}</TextMdBold>
            <RegularTextBlock
              style={{ marginBottom: theme.resolve("spacing_xl") }}
            >
              {t(
                "These files are used to update existing entities with collection data"
              )}
            </RegularTextBlock>
          </>
        )}

        {Object.keys(collectionFileNames).map((collectionName) => {
          const fieldName = replaceDotsWithSpecialCharacter(collectionName);
          return (
            <div
              key={collectionName}
              style={{ marginBottom: theme.resolve("spacing_3xl") }}
            >
              <RegularTextBlock>{`${collectionName}.csv`}</RegularTextBlock>
              <FilePickerUncontrolled
                methodsOfUseForm={methodsOfUseForm}
                placeHolderText={t("Drag and drop {{csv_file}}, OR", {
                  csv_file: `${collectionName}.csv`,
                })}
                name={fieldName}
                accept=".csv"
                required={false}
              />
            </div>
          );
        })}

        <TemplateButtonContainer>
          <SecondaryButtonMediumV2
            onClick={() =>
              dispatch({
                type: "move_back",
                payload: { uploadStage: "upload" } as EntityUploadState,
              })
            }
          >
            {t("Back")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonMediumV2
            loading={isSubmitting}
            type="submit"
            disabled={!formHasSelectedFiles}
            datafor="entity-upload-tip"
            datatip={
              formHasSelectedFiles
                ? ""
                : t("Select at least one file to upload")
            }
          >
            {t("Upload")}
          </PrimaryButtonMediumV2>
        </TemplateButtonContainer>
      </Form>

      <UploadPopups
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onComplete={onComplete}
        message={dialogMessage}
        status={status}
        uploadId={uploadId ?? ""}
      />
    </>
  );
};
